import { BRIEF_LIST_QUERY_KEY } from "@entities/brief/lib/constants";

import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import { IBrief } from "@shared/lib/types/brief";
import { useQueryClient } from "@tanstack/react-query";

import { BackednResponse } from "@shared/lib/types";
import { BriefService } from "../services/brief.service";

export type UpdateBriefFormV2Params = {
  formV2: string;
  campaignName?: string;
  briefId: string;
  /** Skip success toast (autosave) */
  silent?: boolean;
};

export function useUpdateBriefFormV2(options?: {
  onSuccess?: (
    data: BackednResponse<IBrief>,
    variables: UpdateBriefFormV2Params
  ) => void;
}) {
  const queryClient = useQueryClient();
  const {
    mutate: updateForm,
    isLoading: isUpdating,
    isSuccess: isUpdated,
    error: updateFormError,
  } = useMutation({
    mutationFn: async (data: UpdateBriefFormV2Params) => {
      return await BriefService.updateFormV2({
        formV2: data.formV2,
        campaignName: data.campaignName,
        briefId: data.briefId,
      }).catch(async (err) => {
        const responseBody = await err.response.json();
        ToastService.error(responseBody.msg);
        throw err;
      });
    },
    options: {
      skipInvalidation: true,
    },
    onSuccess: (c, data, variables) => {
      if (!variables.silent) {
        ToastService.success("Brief Updated Successfully");
      }
      options?.onSuccess?.(data, variables);
      queryClient.setQueryData(
        [...BRIEF_LIST_QUERY_KEY, variables.briefId],
        (oldData: BackednResponse<IBrief>) => {
          return {
            ...oldData,
            data: {
              ...oldData?.data,
              ...data?.data,
              name: variables.campaignName?.trim() || oldData?.data?.name,
              formV2: variables.formV2,
            },
          };
        }
      );
    },
  });

  return {
    updateForm,
    isUpdating,
    isUpdated,
    updateFormError,
  };
}
